import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ChevronLeft, Users, Copy, Check, Share2, Wallet, Gift, ArrowUpRight, Link2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { tenantSlugFromHost } from '../../utils/tenantDomains';
import referralNormalArt from '../../assets/referral-link-premium-20261031.png';
import referralWalletArt from '../../assets/referral-wallet-premium-20261031.png';
import referralMilestonesArt from '../../assets/referral-milestones-20261009.png';
import referralInviteArt from '../../assets/referral-invite-20261009.png';
import socialShareArt from '../../assets/referral-social-share-20261009.png';
import PlayerLiquidNavigation from './components/PlayerLiquidNavigation';
import PlayerSectionSkeleton from './components/PlayerSectionSkeleton';
import { playerLobbyKey, readPlayerSection, savePlayerSection, playerSessionRevision } from '../../services/playerLobbySession';
import './ReferralPage.css';
import ReferralAnimatedNumber from './components/ReferralAnimatedNumber';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

const formatFp = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})} FP`;

const formatPercent = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}%`;

const ordinal = (value) => {
  const number = Number(value);
  const suffix = number % 100 >= 11 && number % 100 <= 13
    ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th');
  return `${number}${suffix}`;
};

function getTenantHostSlug() {
  return tenantSlugFromHost();
}

export default function ReferralPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const lobbyKey = playerLobbyKey(user);
  const hostTenantSlug = getTenantHostSlug();
  const tenantPathPrefix = hostTenantSlug ? '' : (tenantSlug ? `/${tenantSlug}` : '');
  const requestScope = `${hostTenantSlug || tenantSlug || 'main'}:${user?.id || user?.username || 'guest'}`;
  const homePath = `${tenantPathPrefix}/app`;
  const [activeTab, setActiveTab] = useState('info');
  const [animateNumbers, setAnimateNumbers] = useState(true);
  const [exampleAmount, setExampleAmount] = useState(25);
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState(null); // 'code' | 'link'
  const [brandResult, setBrandResult] = useState({ scope: '', name: 'our games' });
  const brandName = brandResult.scope === requestScope ? brandResult.name : 'our games';
  const [rulesResult, setRulesResult] = useState(() => readPlayerSection(lobbyKey, 'referralRules') || { scope: '', status: 'loading', data: null });
  const [statsResult, setStatsResult] = useState(() => readPlayerSection(lobbyKey, 'referralStats') || { scope: '', status: 'loading', data: null });
  const rulesStatus = rulesResult.scope === requestScope ? rulesResult.status : 'loading';
  const statsStatus = statsResult.scope === requestScope ? statsResult.status : 'loading';
  const rules = rulesStatus === 'ready' ? rulesResult.data : {};
  const stats = statsStatus === 'ready' ? statsResult.data : {};
  // Never synthesize a shareable code: only the server-issued code can recruit.
  const code = String(stats.referral_code || user?.referral_code || '').trim().toUpperCase();
  const encodedCode = encodeURIComponent(code);
  const link = code ? `${window.location.origin}${tenantPathPrefix}/auth?mode=register&ref=${encodedCode}&preview=cosmic2` : '';

  useEffect(() => {
    let mounted = true;
    const revision = playerSessionRevision();
    api.get('/api/referrals/stats', { timeout: 20000 })
      .then(res => {
        if (mounted) {
          const result = { scope: requestScope, status: 'ready', data: res?.data || {} };
          setStatsResult(result); savePlayerSection(lobbyKey, revision, 'referralStats', result);
        }
      })
      .catch(() => {
        if (mounted) setStatsResult({ scope: requestScope, status: 'unavailable', data: null });
      });
    api.get('/api/referrals/rules', {
      timeout: 20000,
      params: { _refresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then(res => {
        if (mounted) {
          const result = { scope: requestScope, status: 'ready', data: res?.data || {} };
          setRulesResult(result); savePlayerSection(lobbyKey, revision, 'referralRules', result);
        }
      })
      .catch(() => {
        if (mounted) setRulesResult({ scope: requestScope, status: 'unavailable', data: null });
      });
    api.get('/api/branding')
      .then(res => {
        if (mounted) setBrandResult({ scope: requestScope, name: res?.data?.brand_name || 'our games' });
      })
      .catch(() => { /* keep fallback */ });
    return () => { mounted = false; };
  }, [requestScope, lobbyKey]);

  const copy = async (text, kind) => {
    try {
      setShareError('');
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      setShareError('Copy is unavailable in this browser. Press and hold your code or link to copy it.');
    }
  };

  const totalReferrals = stats.total_referrals ?? stats.referral_count ?? 0;
  const cashEarned = stats.cash_earned ?? stats.total_cash_earned ?? stats.total_earned ?? 0;
  const freeplayEarned = stats.freeplay_earned ?? stats.fp_earned ?? stats.total_freeplay_earned ?? 0;
  const loadPercent = Number(rules.player_referrer_load_percent || 0);
  const referrerFp = Number(rules.player_referrer_bonus_sc || 0);
  const referredFp = Number(rules.player_referred_bonus_sc || 0);
  const sameSignupFp = Number(referrerFp) === Number(referredFp);
  const rewardMode = rules.player_referral_enabled === false ? 'off' : (
    rules.player_deposit_reward_mode || (Number(rules.player_referrer_load_percent || 0) > 0 ? 'normal' : 'off')
  );
  const milestones = rewardMode === 'milestone'
    ? (Array.isArray(rules.player_deposit_milestone_rewards) ? rules.player_deposit_milestone_rewards : [])
      .filter((row) => row?.enabled !== false && Number(row?.deposit_number) > 0 && Number(row?.percent) > 0)
      .sort((a, b) => Number(a.deposit_number) - Number(b.deposit_number))
    : [];
  const milestonePercentTotal = milestones.reduce((total, row) => total + Number(row.percent), 0);
  const showNormalReward = rulesStatus === 'ready' && rewardMode === 'normal' && loadPercent > 0;
  const showMilestoneReward = rulesStatus === 'ready' && rewardMode === 'milestone' && milestones.length > 0;
  const showSignupFp = rulesStatus === 'ready' && rules.player_referral_enabled !== false && (referrerFp > 0 || referredFp > 0);
  const heroArtMode = rulesStatus !== 'ready' ? 'pending' : showNormalReward ? 'normal' : showMilestoneReward ? 'milestone' : 'invite';
  const heroArt = showNormalReward ? referralNormalArt : showMilestoneReward ? referralMilestonesArt : referralInviteArt;

  const rewardLabel = rules.player_deposit_reward_currency === 'fp' ? 'Freeplay' : 'Main Wallet';
  const exampleReward = (percent) => Math.round(exampleAmount * Number(percent) + Number.EPSILON) / 100;
  const selectTab = (tab, animate) => {
    setAnimateNumbers(animate);
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <div className="player-app referral-page min-h-screen" data-testid="explore-sub-referral">
      <header className="referral-header fixed inset-x-0 top-0 z-50" style={{ zIndex: 50 }} data-testid="referral-header">
        <div className="referral-header-inner">
          <button onClick={() => navigate(homePath)} className="referral-back" data-testid="referral-back" aria-label="Back">
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <div>
            <h1>Referral &amp; Earn</h1>
            <p>Your invitations. Your rewards.</p>
          </div>
          <Users size={24} className="referral-header-icon" aria-hidden="true" />
        </div>
      </header>

      <main className="referral-main">
        <div className="referral-tabs" role="tablist" aria-label="Referral sections">
          {[['info', 'Referral Info'], ['milestones', 'Milestones']].map(([tab, label], index) =>
            <button key={tab} type="button" role="tab" id={`referral-tab-${tab}`}
              aria-selected={activeTab === tab} aria-controls={`referral-panel-${tab}`}
              tabIndex={activeTab === tab ? 0 : -1}
              onClick={event => selectTab(tab, event.detail !== 0)}
              onKeyDown={event => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const next = event.key === 'Home' ? 'info' : event.key === 'End' ? 'milestones' : index === 0 ? 'milestones' : 'info';
                selectTab(next, false);
                document.getElementById(`referral-tab-${next}`)?.focus();
              }}>{label}</button>)}
        </div>
        {rulesStatus === 'loading' || statsStatus === 'loading' ? <PlayerSectionSkeleton label="Loading referral details" rows={5} /> : activeTab === 'info' ? <div id="referral-panel-info" role="tabpanel" aria-labelledby="referral-tab-info" tabIndex={0} key={requestScope}>

        <section className="referral-hero" aria-labelledby="referral-offer-title" data-offer-state={rulesStatus}>
          <div className="referral-hero-copy">
            <h2 id="referral-offer-title">
              {showNormalReward && <>Earn <span>{formatPercent(loadPercent)}</span> on a friend&apos;s first deposit</>}
              {showMilestoneReward && <>Earn up to <span>{formatPercent(milestonePercentTotal)}</span> across their deposits</>}
              {rulesStatus === 'ready' && !showNormalReward && !showMilestoneReward && 'Invite friends to play together'}
              {rulesStatus === 'loading' && 'Checking referral offer'}
              {rulesStatus === 'unavailable' && 'Referral offer unavailable'}
            </h2>
            <p>
              {showNormalReward && `Based on their first successful deposit and paid to your ${rewardLabel === 'Freeplay' ? 'Freeplay balance' : rewardLabel}.`}
              {showMilestoneReward && `Each rate applies only to its own eligible deposit, across ${milestones.length} milestone${milestones.length === 1 ? '' : 's'}.`}
              {rulesStatus === 'loading' && 'Checking the current referral offer…'}
              {rulesStatus === 'unavailable' && 'Reward details could not load. Refresh to check the current offer.'}
              {rulesStatus === 'ready' && !showNormalReward && !showMilestoneReward && 'There is no deposit reward offer active right now. You can still invite friends.'}
            </p>
          </div>
          {rulesStatus === 'ready'
            ? <img src={heroArt} alt="" width="180" height="180" className="referral-hero-art" data-testid="referral-hero-art" data-reward-art-mode={heroArtMode} fetchPriority="high" />
            : rulesStatus === 'loading' ? <div className="referral-art-pending" aria-hidden="true" data-testid="referral-art-pending" /> : null}
          {showSignupFp && <p className="referral-signup-bonus">
            <Gift size={19} aria-hidden="true" />
            <span>{sameSignupFp
              ? <>After your friend verifies their phone, you each receive <strong>{formatFp(referrerFp)}</strong>.</>
              : referrerFp > 0 && referredFp > 0
                ? <>After your friend verifies their phone, you receive <strong>{formatFp(referrerFp)}</strong> and they receive <strong>{formatFp(referredFp)}</strong>.</>
                : referrerFp > 0
                  ? <>After your friend verifies their phone, you receive <strong>{formatFp(referrerFp)}</strong>.</>
                  : <>After your friend verifies their phone, they receive <strong>{formatFp(referredFp)}</strong>.</>}
            </span>
          </p>}
        </section>

        <section className="referral-invite" aria-labelledby="referral-invite-title">
          <h2 id="referral-invite-title">An invite worth sharing</h2>
          <p className="referral-muted">Send your link, or ask your friend to use your code at signup.</p>
          {code ? <>
            <div className="referral-copy-fields">
              <div>
                <label htmlFor="referral-code-value">Your referral code</label>
                <div className="referral-copy-field">
                  <input id="referral-code-value" readOnly value={code} data-testid="ref-code" onFocus={e => e.target.select()} />
                  <button onClick={() => copy(code, 'code')} data-testid="ref-code-copy" aria-label={copied === 'code' ? 'Referral code copied' : 'Copy referral code'}>
                    {copied === 'code' ? <Check size={17} /> : <Copy size={17} />}
                    {copied === 'code' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="referral-link-value">Your invite link</label>
                <div className="referral-copy-field referral-copy-field-link">
                  <input id="referral-link-value" readOnly value={link} data-testid="ref-link" onFocus={e => e.target.select()} />
                  <button onClick={() => copy(link, 'link')} data-testid="ref-link-copy" aria-label={copied === 'link' ? 'Invite link copied' : 'Copy invite link'}>
                    {copied === 'link' ? <Check size={17} /> : <Copy size={17} />}
                    {copied === 'link' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
            <div className="referral-share-row" aria-label="Share your invite link">
              <ShareBtn href={`https://wa.me/?text=${encodeURIComponent(`Join me on ${brandName}! ` + link)}`} label="WhatsApp" spritePosition="0% 0%" />
              <ShareBtn href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`Join me on ${brandName}!`)}`} label="Telegram" spritePosition="100% 0%" />
              <ShareBtn href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`} label="Facebook" spritePosition="0% 100%" />
              <ShareBtn href={`mailto:?subject=${encodeURIComponent(`Join ${brandName}`)}&body=${encodeURIComponent('Sign up with my code ' + code + ' — ' + link)}`} label="Email" spritePosition="100% 100%" />
            </div>
            <button className="referral-native-share" data-testid="ref-share-native" onClick={async () => {
              setShareError('');
              if (navigator.share) {
                try { await navigator.share({ title: brandName, text: 'Join me!', url: link }); }
                catch (error) { if (error?.name !== 'AbortError') setShareError('Sharing is unavailable. Try copying your invite link instead.'); }
              } else { copy(link, 'link'); }
            }}><Share2 size={18} aria-hidden="true" /> Share invite</button>
            <p className="referral-feedback" role="status" aria-live="polite">{shareError || (copied ? `${copied === 'code' ? 'Referral code' : 'Invite link'} copied.` : '')}</p>
          </> : <p className="referral-notice" role="status" data-testid="referral-code-pending">
            {statsStatus === 'loading' ? 'Checking your referral code…' : 'Your referral code is unavailable. Refresh to try again.'}
          </p>}
        </section>

        {(showNormalReward || showMilestoneReward) && <section className="referral-examples" aria-labelledby="referral-examples-title">
          <div className="referral-section-heading"><h2 id="referral-examples-title">See what you could earn</h2><ArrowUpRight size={22} aria-hidden="true" /></div>
          <p className="referral-muted">Choose an example deposit amount. This is an illustration, not your earned balance.</p>
          <fieldset className="referral-amounts">
            <legend>Your friend deposits</legend>
            {[10, 25, 50, 100].map(amount => <button key={amount} type="button" aria-pressed={exampleAmount === amount} onClick={event => { setAnimateNumbers(event.detail !== 0); setExampleAmount(amount); }}>{formatMoney(amount)}</button>)}
          </fieldset>
          {showNormalReward ? <div className="referral-estimate" data-testid="referral-normal-estimate" aria-live="polite">
            <div><span>On their first successful deposit</span><strong>{formatMoney(exampleAmount)} × {formatPercent(loadPercent)}</strong></div>
            <div><span>You could earn</span><strong><ReferralAnimatedNumber value={exampleReward(loadPercent)} format={rules.player_deposit_reward_currency === 'fp' ? 'fp' : 'cash'} animate={animateNumbers} /></strong><small>To your {rewardLabel}</small></div>
          </div> : <div aria-label="Deposit milestone rewards">
            <h3>Your milestone rewards</h3>
            <p className="referral-muted">Each example uses {formatMoney(exampleAmount)} for that deposit. Rates do not stack on a single deposit.</p>
            <ol className="referral-milestones" aria-live="polite">
              {milestones.map(row => {
                const qualifies = exampleAmount >= Number(row.min_deposit_usd || 0);
                return <li key={row.deposit_number}>
                  <div><h4>{ordinal(row.deposit_number)} deposit <span>· {formatPercent(row.percent)}</span></h4><p>{Number(row.min_deposit_usd) > 0 ? `Minimum ${formatMoney(row.min_deposit_usd)}` : 'No minimum deposit'} · Paid to {row.reward_currency === 'fp' ? 'Freeplay' : 'Main Wallet'}</p></div>
                  <strong className={qualifies ? '' : 'referral-ineligible'}>{qualifies ? <ReferralAnimatedNumber value={exampleReward(row.percent)} format={row.reward_currency === 'fp' ? 'fp' : 'cash'} animate={animateNumbers} /> : 'Below minimum'}</strong>
                </li>;
              })}
            </ol>
          </div>}
          <p className="referral-example-note">Calculated from the actual paid deposit, excluding deposit bonuses. Eligibility and applicable team rules still apply. Signup Freeplay is separate.</p>
        </section>}

        </div> : <div id="referral-panel-milestones" role="tabpanel" aria-labelledby="referral-tab-milestones" tabIndex={0} key={requestScope}>
        <section className="referral-earned" aria-labelledby="referral-earned-title">
          <div className="referral-section-heading"><h2 id="referral-earned-title">Your referral progress</h2><span>All time</span></div>
          <p className="referral-muted">Recorded referral earnings only. Estimates are not included.</p>
          {statsStatus === 'ready' ? <dl className="referral-stats">
            <Stat label="Total Referrals" value={totalReferrals} animate={animateNumbers} Icon={Users} />
            <Stat label="Cash Earned" value={cashEarned} format="cash" animate={animateNumbers} Icon={Wallet} />
            <Stat label="Freeplay Earned" value={freeplayEarned} format="fp" animate={animateNumbers} Icon={Gift} />
          </dl> : <p className="referral-notice" role="status">
            {statsStatus === 'loading' ? 'Checking earned rewards…' : 'Earned rewards could not load right now.'}
          </p>}
        </section>

        {statsStatus === 'ready' && Number(totalReferrals) === 0 && <p className="referral-notice">Your first referral starts with an invite. Share your personal link from Referral Info.</p>}
        <section className="referral-how" aria-labelledby="referral-how-title">
          <div className="referral-how-heading"><div><h2 id="referral-how-title">From invite to reward</h2><p className="referral-muted">A little clarity before you share.</p></div><img src={referralWalletArt} alt="" width="100" height="100" loading="lazy" /></div>
          <ol className="referral-steps">
            <li><Link2 aria-hidden="true" size={21} /><div><h3>Share your personal invite</h3><p>Your friend signs up using your link or enters your referral code.</p></div></li>
            <li><Users aria-hidden="true" size={21} /><div><h3>They complete the qualifying steps</h3><p>{showNormalReward ? 'The deposit reward applies to their first successful deposit only, not every later load.' : showMilestoneReward ? 'They complete the eligible deposits listed in Referral Info. Each deposit milestone has its own rate and minimum.' : 'Check the current offer in Referral Info before expecting a deposit reward.'}{showSignupFp && ' Phone verification is required for the signup Freeplay reward.'}</p></div></li>
            <li><Wallet aria-hidden="true" size={21} /><div><h3>Keep track of your earnings</h3><p>Cash and Freeplay earnings are shown separately. An invite or an example calculation is not a credited reward.</p></div></li>
          </ol>
          <details className="referral-details"><summary>Why might a reward not appear yet?</summary><p>Check that your friend used your code at signup and completed the qualifying steps. Pending or unsuccessful deposits do not qualify. If a completed reward is missing, contact your team&apos;s support with the deposit details.</p></details>
        </section>
        </div>}
      </main>
      <PlayerLiquidNavigation active="referrals" />
    </div>
  );
}

function Stat({ label, value, Icon, format = 'number', animate = true }) {
  return <div className="referral-stat"><Icon size={19} aria-hidden="true" /><dt>{label}</dt><dd><ReferralAnimatedNumber value={value} format={format} animate={animate} /></dd></div>;
}

function ShareBtn({ href, label, spritePosition }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="referral-share-channel" data-testid={`ref-share-${label.toLowerCase()}`}>
    <span className="referral-social-icon" style={{ backgroundImage: `url(${socialShareArt})`, backgroundPosition: spritePosition, backgroundSize: '200% 200%', backgroundRepeat: 'no-repeat' }} aria-hidden="true" />
    <span>{label}</span>
  </a>;
}
